import type { ReactNode } from 'react';
import type { ProductListItem } from '../../model/types';
import { Clock4, ImageOff, MapPin } from 'lucide-react';
import { joinCss } from '@/shared/lib/css-utils';
import { Badge, Checkbox, TableRow, Text } from '@/shared/ui';
import { useAdultTravellerTypeIds } from '../../api/use-adult-traveller-type-ids';
import { getCategoryIcon } from '../../lib/category-icon';
import { extractDurationHours } from '../../lib/duration';
import { extractDisplayPrice, formatPrice } from '../../lib/price';
import { resolveThumbnailUrl } from '../../lib/thumbnail-url';
import { resolveProductTitle } from '../../lib/title';
import css from './product-row.module.scss';

const resolveLocation = (product: ProductListItem): string => {
  if (product.city && product.country)
    return `${product.city}, ${product.country}`;
  return product.city ?? product.country ?? '—';
};

type ProductRowProps = {
  product: ProductListItem;
  onSelect?: (id: string) => void;
  // Row hover → host can highlight the matching map marker (list↔map sync).
  // Fires the product id on enter, `null` on leave. Optional — callers
  // without a map omit it.
  onHoverChange?: (id: string | null) => void;
  // Per-row quick actions rendered in a trailing cell (edit / visibility /
  // overflow menu). The host owns the actions widget so the entity stays
  // free of feature imports.
  actionsSlot?: ReactNode;
  // Multi-select checkbox. Rendered only when `onToggleSelect` is supplied;
  // the host owns the selection store.
  selected?: boolean;
  onToggleSelect?: () => void;
};

export const ProductRow = ({
  product,
  onSelect,
  onHoverChange,
  actionsSlot,
  selected,
  onToggleSelect,
}: ProductRowProps) => {
  const title = resolveProductTitle(product.title);
  const location = resolveLocation(product);
  const CategoryIcon = getCategoryIcon(product.contentCategory);
  const durationHours = extractDurationHours(product.legs);
  const adultTravellerIds = useAdultTravellerTypeIds();
  const displayPrice = extractDisplayPrice(product.price, adultTravellerIds);
  const priceText = formatPrice(displayPrice, product.currency);

  const handleSelect = () => onSelect?.(product.id);
  const thumbnailUrl = resolveThumbnailUrl(product.thumbnail);

  return (
    // Card-style row (report §2.2): a large thumbnail on the left, then a
    // content block with the title on top and the metadata (location ·
    // category · duration · price) flowing beneath it. The title is never
    // truncated or hidden — it owns the top line of the content block and
    // wraps if long. `TableRow` still gives the hover / click / border.
    <TableRow
      className={joinCss(
        css.root,
        onSelect ? css.clickable : false,
        actionsSlot ? css.hasActions : false,
        selected && !onToggleSelect ? css.sourceSelected : false
      )}
      onClick={onSelect ? handleSelect : undefined}
      onMouseEnter={onHoverChange ? () => onHoverChange(product.id) : undefined}
      onMouseLeave={onHoverChange ? () => onHoverChange(null) : undefined}
      onKeyDown={
        onSelect
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelect();
              }
            }
          : undefined
      }
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      aria-current={selected && !onToggleSelect ? 'true' : undefined}
    >
      {/* Thumbnail with the selection checkbox overlaid in its top-left. */}
      <div className={css.thumb}>
        {thumbnailUrl ? (
          <img
            alt=""
            className={css.thumbImg}
            loading="lazy"
            src={thumbnailUrl}
          />
        ) : (
          <ImageOff className={css.thumbPlaceholder} size={20} />
        )}
        {onToggleSelect ? (
          // Stop the row's open-detail click from firing when (de)selecting.
          // biome-ignore lint/a11y/noStaticElementInteractions: click-guard wrapper
          // biome-ignore lint/a11y/useKeyWithClickEvents: Checkbox owns keyboard handling
          <span className={css.selectBox} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              aria-label="Select product"
              checked={!!selected}
              onChange={onToggleSelect}
            />
          </span>
        ) : null}
      </div>

      {/* Content block: title on top, metadata flowing beneath. */}
      <div className={css.content}>
        <div className={css.titleRow}>
          <Text className={css.titleText} weight="medium">
            {title}
          </Text>
          {product.isHiddenOnCms ? (
            <Badge variant="error">CMS Hidden</Badge>
          ) : null}
          {product.isHiddenOnMiniPlanner ? (
            <Badge variant="error">Mini Planner Hidden</Badge>
          ) : null}
        </div>
        <div className={css.meta}>
          <span className={css.metaItem}>
            <MapPin className={css.metaIcon} size={15} />
            <Text variant="muted">{location}</Text>
          </span>
          {product.contentCategory ? (
            <span className={css.metaItem}>
              <CategoryIcon className={css.metaIcon} size={15} />
              <Text variant="muted">{product.contentCategory}</Text>
            </span>
          ) : null}
          <span className={css.metaItem}>
            <Clock4 className={css.metaIcon} size={15} />
            <Text variant="muted">
              {durationHours != null
                ? `${durationHours} ${durationHours === 1 ? 'hour' : 'hours'}`
                : '—'}
            </Text>
          </span>
          <span className={css.metaItem}>
            <Text weight="medium">{priceText}</Text>
          </span>
        </div>
      </div>

      {actionsSlot ? (
        // Guard the whole overlay (incl. its gradient/padding) so clicking
        // anywhere in the action area never falls through to the row's
        // open-detail handler.
        // biome-ignore lint/a11y/noStaticElementInteractions: click-guard wrapper, not an interactive control
        // biome-ignore lint/a11y/useKeyWithClickEvents: guard only; the action buttons own their own keyboard handling
        <div
          className={css.actionsOverlay}
          onClick={(e) => e.stopPropagation()}
        >
          {actionsSlot}
        </div>
      ) : null}
    </TableRow>
  );
};
